import { FEEDBACK_KIND_LABEL, FEEDBACK_KINDS, FEEDBACK_MAX_CHARS, type FeedbackKind } from '@concord/domain';
import { Button, Field, Icon, Notice } from '@concord/ui';
import { useMutation } from '@tanstack/react-query';
import { Popover } from 'radix-ui';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import s from './feedback.module.css';

const PROMPT: Record<FeedbackKind, string> = {
  feedback: "What's on your mind?",
  bug: 'What went wrong, and what did you expect?',
  idea: 'What would make this better?',
};

/**
 * The floating Feedback button, bottom right on every screen. Sends the kind, the message
 * and the page address; the server adds the IP and, when signed in, the account.
 */
export function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<FeedbackKind>('feedback');
  const [message, setMessage] = useState('');
  const send = useMutation({
    mutationFn: () => api('/feedback', { body: { kind, message, url: window.location.href } }),
    onSuccess: () => setMessage(''),
  });
  const reset = (v: boolean) => {
    setOpen(v);
    if (!v && send.isSuccess) send.reset();
  };

  return (
    <Popover.Root open={open} onOpenChange={reset}>
      <Popover.Trigger asChild>
        <button type="button" className={s.fab} aria-label="Send feedback">
          <Icon.Feedback />
          <span>Feedback</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={s.panel}
          side="top"
          align="end"
          sideOffset={10}
          aria-label="Send feedback"
        >
          {send.isSuccess ? (
            <div className={s.done} role="status">
              <strong>Thank you. It's been sent.</strong>
              <p>The Cancer Expert Now team reads every message.</p>
              <Button variant="sec" onClick={() => reset(false)}>
                Close
              </Button>
            </div>
          ) : (
            <form
              className={s.form}
              onSubmit={(e) => {
                e.preventDefault();
                if (message.trim()) send.mutate();
              }}
            >
              <div className={s.head}>
                <strong>Send feedback</strong>
                <Popover.Close className={s.x} aria-label="Close">
                  ×
                </Popover.Close>
              </div>
              <div className={s.kinds} role="radiogroup" aria-label="Kind">
                {FEEDBACK_KINDS.map((k) => (
                  // biome-ignore lint/a11y/useSemanticElements: a segmented control styled as buttons
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={kind === k}
                    className={kind === k ? s.on : undefined}
                    onClick={() => setKind(k)}
                  >
                    {FEEDBACK_KIND_LABEL[k]}
                  </button>
                ))}
              </div>
              <Field
                label={PROMPT[kind]}
                hint={`${message.length.toLocaleString('en-US')} of ${FEEDBACK_MAX_CHARS.toLocaleString('en-US')} characters`}
              >
                {(id, d) => (
                  <textarea
                    id={id}
                    aria-describedby={d}
                    className="textarea"
                    maxLength={FEEDBACK_MAX_CHARS}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                )}
              </Field>
              <p className={s.note}>
                We send this page's address and your IP address with your message. For anything about your
                health, contact your care navigator or your own doctor.
              </p>
              {send.isError && <Notice tone="bad">{errorMessage(send.error)}</Notice>}
              <div className={s.actions}>
                <Button type="submit" busy={send.isPending} disabled={!message.trim()}>
                  Send
                </Button>
              </div>
            </form>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
